import { z } from 'zod';

export const APP_ENVIRONMENTS = ['development', 'staging', 'production'] as const;
export type AppEnvironment = (typeof APP_ENVIRONMENTS)[number];

/**
 * Detects keys that must never reach the browser: legacy JWT service-role keys
 * and the new `sb_secret_` API keys. Only anon/publishable keys are allowed.
 */
export function isPrivilegedSupabaseKey(key: string): boolean {
  if (key.startsWith('sb_secret_')) return true;

  const payload = key.split('.')[1];
  if (!payload) return false;
  try {
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const decoded: unknown = JSON.parse(atob(normalized));
    return (
      typeof decoded === 'object' &&
      decoded !== null &&
      'role' in decoded &&
      decoded.role === 'service_role'
    );
  } catch {
    return false;
  }
}

const envSchema = z.object({
  VITE_SUPABASE_URL: z.url({
    protocol: /^https?$/,
    error: 'VITE_SUPABASE_URL debe ser una URL http(s) válida.',
  }),
  VITE_SUPABASE_ANON_KEY: z
    .string({ error: 'VITE_SUPABASE_ANON_KEY es obligatorio.' })
    .trim()
    .min(1, 'VITE_SUPABASE_ANON_KEY es obligatorio.')
    .refine((key) => !isPrivilegedSupabaseKey(key), {
      message:
        'VITE_SUPABASE_ANON_KEY contiene una clave privilegiada (service role / secret). Usa solo la clave anon o publishable.',
    }),
  VITE_APP_ENV: z.enum(APP_ENVIRONMENTS).default('development'),
});

export type AppEnv = z.infer<typeof envSchema>;

export class EnvValidationError extends Error {
  readonly issues: string[];

  constructor(issues: string[]) {
    super(`Configuración de entorno inválida:\n- ${issues.join('\n- ')}`);
    this.name = 'EnvValidationError';
    this.issues = issues;
  }
}

/** Validates a raw env record. Pure: exported for tests and tooling. */
export function parseEnv(source: Record<string, unknown>): AppEnv {
  const result = envSchema.safeParse(source);
  if (!result.success) {
    throw new EnvValidationError(result.error.issues.map((issue) => issue.message));
  }
  return result.data;
}

let cachedEnv: AppEnv | undefined;

/**
 * Returns the validated browser environment. Validation is lazy so the
 * application shell can render (and report a clear error) even when the
 * Supabase configuration is missing.
 */
export function getEnv(): AppEnv {
  cachedEnv ??= parseEnv(import.meta.env);
  return cachedEnv;
}

/** Environment label that never throws; used for non-critical UI hints. */
export function getAppEnvironment(): AppEnvironment {
  const parsed = z.enum(APP_ENVIRONMENTS).safeParse(import.meta.env.VITE_APP_ENV);
  return parsed.success ? parsed.data : 'development';
}
